//? Import necessary dependencies
import { useState, useCallback } from "react";

//* Importing the necessary dependencies from the react-query library
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import the questionAPI object from the questionsAPI file
import { questionAPI } from "../questions/questionsAPI";

//? Import the necessary types from the types file
import { type Pagination } from "../../services/stores/types";

//? Import the QuestionType type from the form/types/types.tsx file
import {
	type CreateQuestionInput,
	type UpdateQuestionInput,
	type QuestionDetails,
	GetQuestionsDisciplinesResponse,
} from "./types/types";

//? Import the QuestionType type from the form/types/types.tsx file
import { type QuestionType } from "../../hooks/questions/types/types";

export const useQuestions = (
	token: string,
	options?: { ownOnly?: boolean; bancaOnly?: boolean }
) => {
	const queryClient = useQueryClient();

	const ownOnly = options?.ownOnly ?? false;
	const bancaOnly = options?.bancaOnly ?? false;

	const [errorQuestion, setErrorQuestion] = useState<boolean>(false);

	const [filters, setFilters] = useState<{
		disciplineId?: string;
		year?: number;
		phase?: string;
		createdBy?: string;
	}>({});

	const [search, setSearch] = useState<string>("");
	const [pagination, setPagination] = useState<Pagination>({
		currentPage: 1,
		totalPages: 1,
		itemsPerPage: 10,
		totalItems: 0,
	});

	const queryKey = [
		"questions",
		{
			search,
			currentPage: pagination.currentPage,
			itemsPerPage: pagination.itemsPerPage,
			ownOnly,
			bancaOnly,
			...filters,
		},
	] as const;

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
		queryKey,
		queryFn: () =>
			questionAPI.getAll(
				search,
				pagination.currentPage,
				token,
				pagination.itemsPerPage,
				filters.disciplineId,
				filters.year,
				filters.phase,
				bancaOnly,
				undefined,
				undefined,
				ownOnly,
				filters.createdBy
			),
		enabled: !!token,
		staleTime: 5 * 60 * 1000,
	});

	const handleFilters = useCallback(
		(
			newFilters: Partial<{
				disciplineId?: string;
				year?: number;
				phase?: string;
				createdBy?: string;
			}>
		) => {
			setFilters((prevFilters) => ({
				...prevFilters,
				...newFilters,
			}));
			setPagination((prev) => ({ ...prev, currentPage: 1 }));
		},
		[]
	);

	const updateFilters = useCallback(
		(
			newFilters: Partial<{
				disciplineId?: string;
				year?: number;
				phase?: string;
				createdBy?: string;
			}>
		) => {
			setFilters((prevFilters) => ({
				...prevFilters,
				...newFilters,
			}));
			setPagination((prev) => ({ ...prev, currentPage: 1 }));
		},
		[setFilters, setPagination]
	);

	const questions = data?.questions || [];
	const fetchedPagination = data?.pagination || pagination;

	const handleSearch = useCallback((searchTerm: string) => {
		setSearch(searchTerm);
		setPagination((prev) => ({ ...prev, currentPage: 1 }));
	}, []);

	const changePage = useCallback((newPage: number) => {
		setPagination((prev) => ({ ...prev, currentPage: newPage }));
	}, []);

	const createQuestionMutation = useMutation({
		mutationFn: async (questionData: CreateQuestionInput) => {
			const createdQuestion = await questionAPI.create(questionData, token);
			return createdQuestion;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["questions"] });
		},
	});

	const updateQuestionMutation = useMutation({
		mutationFn: (questionData: UpdateQuestionInput) =>
			questionAPI.update(questionData.id, questionData, token),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["questions"] });
		},
		onError: (error) => {
			setErrorQuestion(true);
		},
	});

	const deleteQuestionMutation = useMutation({
		mutationFn: async (id: string) => await questionAPI.delete(id, token),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["questions"] });
		},
		onError: (error) => {
			console.error("Error deleting question:", error);
		},
	});

	const fetchQuestionsByDiscipline = useCallback(
		async (
			disciplineId: string,
			questionType: QuestionType,
			page: number = 1,
			perPage: number = 10,
			search?: string,
			tags?: string[]
		): Promise<GetQuestionsDisciplinesResponse | null> => {
			try {
				return await questionAPI.getByDisciplineId(
					disciplineId,
					token,
					page,
					perPage,
					questionType,
					search,
					tags
				);
			} catch (err) {
				console.error("Error fetching questions by discipline:", err);
				return null;
			}
		},
		[token]
	);

	const getQuestionById = useCallback(
		async (id: string): Promise<QuestionDetails | null> => {
			try {
				const questionDetails = await questionAPI.getById(id, token);
				return questionDetails;
			} catch (err) {
				console.error("Error fetching discipline by ID:", err);
				throw err;
			}
		},
		[token]
	);

	return {
		questions,
		loading: isLoading || isFetching,
		error: isError ? error?.message : null,
		search,
		errorQuestion,
		setSearch: handleSearch,
		pagination: fetchedPagination,
		filters,
		setFilters: handleFilters,
		updateFilters,
		setPagination,
		refetch,
		getQuestionById,
		createQuestion: createQuestionMutation.mutateAsync,
		updateQuestion: updateQuestionMutation.mutateAsync,
		deleteQuestion: deleteQuestionMutation.mutateAsync,
		fetchQuestionsByDiscipline,
		changePage,
		isCreating: createQuestionMutation.isPending,
		isUpdating: updateQuestionMutation.isPending,
		isDeleting: deleteQuestionMutation.isPending,
	};
};
